// Shared Smart Cards Data - Unified data structure for smart cards

export interface Recommendation {
  type: 'optimization' | 'partnership' | 'financing' | 'alert' | 'urgent' | 'opportunity';
  title: string;
  description: string;
  impact: 'high' | 'medium' | 'low';
  savings?: string;
  amount?: string;
  action?: string;
}

export interface SmartCardInsight {
  id: string;
  type: 'optimization' | 'prediction' | 'opportunity' | 'alert' | 'urgent';
  title: string;
  description: string;
  confidence: number;
  impact: 'high' | 'medium' | 'low';
  category: string;
  actionRequired: boolean;
  estimatedSavings?: number;
  estimatedRevenue?: number;
  timeframe: string;
  priority: number;
}

export interface SmartCardMetrics {
  budget: number;
  spent: number;
  efficiency: number;
  roi: number;
  trend: number;
}

export interface SmartCard {
  id: string;
  title: string;
  type: 'department' | 'branch' | 'project' | 'campaign';
  status: 'active' | 'warning' | 'optimized' | 'critical';
  aiScore: number;
  insights: SmartCardInsight[];
  metrics: SmartCardMetrics;
  lastAnalyzed: Date;
  nextAnalysis: Date;
  // Additional properties for preview display
  budget: string;
  spent: string;
  remaining: string;
  utilization: number;
  trend: string;
  trendValue: string;
  alerts: number;
  recommendations: number;
  color: string;
  bgColor: string;
  performance: string;
  lastUpdate: string;
  budgetBreakdown: {
    allocated: number;
    spent: number;
    remaining: number;
    percentage: number;
  };
  customRecommendations: Recommendation[];
}

// Utility function to format currency
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('ar-SA', {
    style: 'currency',
    currency: 'SAR',
    minimumFractionDigits: 0
  }).format(amount);
};

// Main smart cards data
export const smartCardsData: SmartCard[] = [
  {
    id: '1',
    title: 'قسم التسويق الرقمي',
    type: 'department',
    status: 'warning',
    aiScore: 78,
    insights: [
      {
        id: '1-1',
        type: 'optimization',
        title: 'تحسين إعلانات جوجل',
        description: 'اكتشف الذكاء الاصطناعي فرصة لتوفير 15% من ميزانية الإعلانات عبر إعادة توزيع الكلمات المفتاحية',
        confidence: 92,
        impact: 'high',
        category: 'إعلانات',
        actionRequired: true,
        estimatedSavings: 12750,
        timeframe: '30 يوم',
        priority: 1
      },
      {
        id: '1-2',
        type: 'prediction',
        title: 'توقع زيادة التحويلات',
        description: 'النموذج التنبؤي يشير إلى إمكانية زيادة التحويلات بنسبة 23% خلال الشهر القادم',
        confidence: 87,
        impact: 'high',
        category: 'تحويلات',
        actionRequired: false,
        estimatedRevenue: 45000,
        timeframe: '30 يوم',
        priority: 2
      }
    ],
    metrics: {
      budget: 85000,
      spent: 62400,
      efficiency: 73,
      roi: 2.4,
      trend: 12
    },
    lastAnalyzed: new Date(Date.now() - 10 * 60 * 1000),
    nextAnalysis: new Date(Date.now() + 2 * 60 * 60 * 1000),
    // Preview display properties
    budget: '85,000 ر.س',
    spent: '62,400 ر.س',
    remaining: '22,600 ر.س',
    utilization: 73,
    trend: 'up',
    trendValue: '+12%',
    alerts: 1,
    recommendations: 2,
    color: 'from-blue-500 to-blue-600',
    bgColor: 'bg-blue-50 dark:bg-blue-900/20',
    performance: 'good',
    lastUpdate: 'منذ 10 دقائق',
    budgetBreakdown: {
      allocated: 85000,
      spent: 62400,
      remaining: 22600,
      percentage: 73.4
    },
    customRecommendations: [
      {
        type: 'optimization',
        title: 'تحسين إعلانات جوجل',
        description: 'يمكن توفير 3,200 ر.س شهرياً عبر تحسين الكلمات المفتاحية',
        impact: 'high',
        savings: '3,200 ر.س'
      },
      {
        type: 'partnership',
        title: 'خصم Snap Business',
        description: 'خصم 15% متاح على إعلانات سناب شات للشركات',
        impact: 'medium',
        savings: '1,800 ر.س'
      }
    ]
  },
  {
    id: '2',
    title: 'مشروع التوسع الجديد',
    type: 'project',
    status: 'optimized',
    aiScore: 94,
    insights: [
      {
        id: '2-1',
        type: 'opportunity',
        title: 'فرصة شراكة استراتيجية',
        description: 'تحليل السوق يظهر فرصة شراكة مع موردين محليين يمكن أن توفر 25% من التكاليف',
        confidence: 89,
        impact: 'high',
        category: 'شراكات',
        actionRequired: true,
        estimatedSavings: 180000,
        timeframe: '90 يوم',
        priority: 1
      }
    ],
    metrics: {
      budget: 750000,
      spent: 425000,
      efficiency: 89,
      roi: 3.2,
      trend: 18
    },
    lastAnalyzed: new Date(Date.now() - 5 * 60 * 1000),
    nextAnalysis: new Date(Date.now() + 4 * 60 * 60 * 1000),
    // Preview display properties
    budget: '750,000 ر.س',
    spent: '425,000 ر.س',
    remaining: '325,000 ر.س',
    utilization: 57,
    trend: 'up',
    trendValue: '+18%',
    alerts: 0,
    recommendations: 1,
    color: 'from-green-500 to-green-600',
    bgColor: 'bg-green-50 dark:bg-green-900/20',
    performance: 'excellent',
    lastUpdate: 'منذ 5 دقائق',
    budgetBreakdown: {
      allocated: 750000,
      spent: 425000,
      remaining: 325000,
      percentage: 56.7
    },
    customRecommendations: [
      {
        type: 'opportunity',
        title: 'فرصة شراكة محلية',
        description: 'شراكة مع موردين محليين توفر 25% من التكاليف',
        impact: 'high',
        savings: '180,000 ر.س'
      }
    ]
  },
  {
    id: '3',
    title: 'فرع الرياض الرئيسي',
    type: 'branch',
    status: 'active',
    aiScore: 85,
    insights: [
      {
        id: '3-1',
        type: 'optimization',
        title: 'تحسين كفاءة الطاقة',
        description: 'تحليل استهلاك الطاقة يظهر إمكانية توفير 20% من فواتير الكهرباء',
        confidence: 91,
        impact: 'medium',
        category: 'طاقة',
        actionRequired: true,
        estimatedSavings: 8400,
        timeframe: '60 يوم',
        priority: 2
      }
    ],
    metrics: {
      budget: 320000,
      spent: 245000,
      efficiency: 85,
      roi: 2.8,
      trend: 8
    },
    lastAnalyzed: new Date(Date.now() - 15 * 60 * 1000),
    nextAnalysis: new Date(Date.now() + 3 * 60 * 60 * 1000),
    // Preview display properties
    budget: '320,000 ر.س',
    spent: '245,000 ر.س',
    remaining: '75,000 ر.س',
    utilization: 77,
    trend: 'up',
    trendValue: '+8%',
    alerts: 0,
    recommendations: 1,
    color: 'from-purple-500 to-purple-600',
    bgColor: 'bg-purple-50 dark:bg-purple-900/20',
    performance: 'good',
    lastUpdate: 'منذ 15 دقيقة',
    budgetBreakdown: {
      allocated: 320000,
      spent: 245000,
      remaining: 75000,
      percentage: 76.6
    },
    customRecommendations: [
      {
        type: 'optimization',
        title: 'تحسين كفاءة الطاقة',
        description: 'توفير 20% من فواتير الكهرباء عبر تحسين الإضاءة والتكييف',
        impact: 'medium',
        savings: '8,400 ر.س'
      }
    ]
  },
  {
    id: '4',
    title: 'حملة العروض الصيفية',
    type: 'campaign',
    status: 'critical',
    aiScore: 45,
    insights: [
      {
        id: '4-1',
        type: 'alert',
        title: 'تجاوز الميزانية المحددة',
        description: 'الحملة تجاوزت 95% من الميزانية المخصصة مع تبقي 10 أيام',
        confidence: 98,
        impact: 'high',
        category: 'ميزانية',
        actionRequired: true,
        timeframe: 'فوري',
        priority: 1
      },
      {
        id: '4-2',
        type: 'urgent',
        title: 'انخفاض معدل التحويل',
        description: 'معدل التحويل انخفض بنسبة 35% مقارنة بالأسبوع الماضي',
        confidence: 94,
        impact: 'high',
        category: 'أداء',
        actionRequired: true,
        timeframe: 'فوري',
        priority: 1
      }
    ],
    metrics: {
      budget: 45000,
      spent: 42750,
      efficiency: 45,
      roi: 1.2,
      trend: -15
    },
    lastAnalyzed: new Date(Date.now() - 2 * 60 * 1000),
    nextAnalysis: new Date(Date.now() + 30 * 60 * 1000),
    // Preview display properties
    budget: '45,000 ر.س',
    spent: '42,750 ر.س',
    remaining: '2,250 ر.س',
    utilization: 95,
    trend: 'down',
    trendValue: '-15%',
    alerts: 2,
    recommendations: 2,
    color: 'from-red-500 to-red-600',
    bgColor: 'bg-red-50 dark:bg-red-900/20',
    performance: 'poor',
    lastUpdate: 'منذ دقيقتين',
    budgetBreakdown: {
      allocated: 45000,
      spent: 42750,
      remaining: 2250,
      percentage: 95.0
    },
    customRecommendations: [
      {
        type: 'urgent',
        title: 'إيقاف الحملة فوراً',
        description: 'تجاوز الميزانية مع انخفاض الأداء يتطلب إيقاف فوري',
        impact: 'high',
        action: 'إيقاف فوري'
      },
      {
        type: 'alert',
        title: 'مراجعة استراتيجية الحملة',
        description: 'تحليل أسباب انخفاض معدل التحويل وإعادة التخطيط',
        impact: 'high',
        action: 'مراجعة عاجلة'
      }
    ]
  }
];

// Helper functions
export const getCardById = (id: string): SmartCard | undefined => {
  return smartCardsData.find(card => card.id === id);
};

export const getCardsByType = (type: string): SmartCard[] => {
  return smartCardsData.filter(card => card.type === type);
};

export const getCardsByStatus = (status: string): SmartCard[] => {
  return smartCardsData.filter(card => card.status === status);
};
